import React from 'react';
import { Link } from 'react-router-dom';
import styles from './sections.module.css';
import ExpandableCard from '../ExpandableCard';
import { CheckIcon } from '../MarketingIcons';

/**
 * Outcome groups rather than a flat inventory. Platform first; the card is
 * present, not first. Anything not yet built sits behind "What's coming".
 */
const GROUPS = [
  {
    id: 'capture',
    title: 'Capture',
    summary: 'Share without an app for them. Guest capture. Paper cards into the same place.',
    items: [
      ['Always-current profile', 'update once, everyone sees the change'],
      ['Guest capture', 'collect details from people with no Velox account'],
      ['No app for the recipient', 'your card or link opens in their browser'],
      ['Paper card scanning', 'photograph old cards straight into your contacts'],
      ['Invitations to connect', 'invite a scanned contact onto Velox by email'],
      ['Contactless NFC card', 'tap any phone to share your profile'],
    ],
  },
  {
    id: 'remember',
    title: 'Remember',
    summary: 'Live details, meeting place and date, private notes, a map of the year.',
    items: [
      ['Contacts that update themselves', 'their new email reaches your copy'],
      ['Meeting context', 'where and when each contact was made'],
      ['Contact map', 'a year of introductions, by place and date'],
      ['Private notes', 'visible only to the person who wrote them'],
      ['Contact archive', 'keep past contacts without cluttering your list'],
    ],
  },
  {
    id: 'act',
    title: 'Act',
    summary: 'Find people again. Profile and tap analytics. Industry briefings and events.',
    items: [
      ['Profile analytics', 'views, link clicks and card taps over time'],
      ['Weekly market digest', 'written for your industry, sent to your inbox'],
      ['Daily brief', 'a morning summary of the topics you asked us to watch'],
      ['Event discovery', 'conferences and meet-ups matched to your industry'],
    ],
  },
  {
    id: 'teams',
    title: 'For teams',
    summary: 'Shared hub, seats, comparison across people and teams, branded cards.',
    items: [
      ['Company contact hub', 'org-wide contacts, deduplicated and searchable'],
      ['Teams and seats', 'group people and manage allocation centrally'],
      ['Email invites', 'onboard colleagues with expiring invite links'],
      ['Team and member analytics', 'leaderboards and team-by-team comparison'],
      ['Audit-logged access', 'every bulk view of contact data is recorded'],
      ['Customised metal cards', 'designed with you, or produced from your artwork'],
    ],
  },
];

const BUILDING = [
  ['Automated AI follow-ups', 'drafted from your notes, prompted while it is warm'],
  ['Role-based seats', 'the account stays with the position, not the person'],
  ['Leaver handover', 'a successor inherits the contacts, access is revoked'],
  ['Automated org charting', 'drawn from your teams and reporting lines'],
  ['Meetings booked', 'confirmed meetings tracked, not just invitations sent'],
  ['Event outcome reporting', 'which occasions and event types produced business'],
  ['Network attendance', 'who else from your network is going'],
  ['Card customisation preview', 'design and order your card on the site'],
  ['Team card ordering', 'order and reorder cards for every seat in one go'],
];

const FeatureList = ({ items, muted = false }) => (
  <ul className={`${styles.featureList} ${muted ? styles.featureListMuted : ''}`}>
    {items.map(([name, detail]) => (
      <li key={name} className={styles.featureItem}>
        <CheckIcon className={styles.featureCheck} width={18} height={18} />
        <span>
          <strong>{name}</strong>: {detail}
        </span>
      </li>
    ))}
  </ul>
);

const FeaturesSection = () => (
  <section className="vxm-section vxm-section--raised" id="features">
    <div className="vxm-container">
      <div className="vxm-section-head vxm-reveal">
        <p className="vxm-eyebrow">Features</p>
        <h2 className="vxm-h2">Keep introductions useful</h2>
        <p className="vxm-body">
          Everything you need to keep introductions useful. Cards are included;
          they are not the product.
        </p>
      </div>

      <div className={styles.featureGroups}>
        {GROUPS.map((group) => (
          <div key={group.id} className="vxm-reveal">
            <ExpandableCard
              title={group.title}
              summary={group.summary}
            >
              <FeatureList items={group.items} />
            </ExpandableCard>
          </div>
        ))}
      </div>

      <div className={`${styles.comingBlock} vxm-reveal`}>
        <ExpandableCard
          title="What's coming"
          summary="Committed, labelled, and not mixed in with what you can use today."
          status="Committed"
        >
          <FeatureList items={BUILDING} muted />
        </ExpandableCard>
      </div>

      <p className="vxm-body vxm-reveal" style={{ marginTop: 'var(--vxm-space-6)' }}>
        <Link to="/product" className="vxm-link">
          Explore the platform
        </Link>
      </p>
    </div>
  </section>
);

export default FeaturesSection;
